import {
    createTheme,
    unstable_createBreakpoints as createBreakpoints,
} from "@mui/material/styles"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

interface ISizeClass {
    [key: string]: number
}

interface IColorSetting {
    color: string
    light: string
    dark: string
    text: string
}

export interface IColorPresets {
    primary: IColorSetting
    secondary: IColorSetting
    background: IColorSetting

    [key: string]: IColorSetting
}

export interface ITheme {
    colors: IColorPresets
    icon: {
        color: string
        size: ISizeClass
    }
}

const breakpoints = createBreakpoints({})

type ComponentSettings = Record<string, Record<string, unknown>>

const createComponents = (
    defaultProps: ComponentSettings,
    styleOverrides: ComponentSettings,
) =>
    Object.fromEntries(
        Array.from(
            new Set([
                ...Object.keys(defaultProps),
                ...Object.keys(styleOverrides),
            ]),
        ).map((component) => [
            component,
            {
                ...(defaultProps[component] && {
                    defaultProps: defaultProps[component],
                }),
                ...(styleOverrides[component] && {
                    styleOverrides: styleOverrides[component],
                }),
            },
        ]),
    )

export const brightSettings = {
    palette: {
        action: {
            selected: "#f95744",
            hover: "#fff",
            disabled: "#9B9B9B",
        },
        primary: {
            light: "#FF916D",
            main: "#f95744",
            dark: "#D92D12",
            contrastText: "#fff", // getContrastText(palette.primary[500]),
        },
        secondary: {
            light: "#b6d1cf",
            main: "#edeff1",
            dark: "#597170",
            contrastText: "#000",
        },
        tertiary: {
            light: "#335d73",
            main: "#00263c",
            dark: "#000e1a",
            contrastText: "#fff",
        },
        error: {
            light: "#f44",
            main: "#f44",
            dark: "#f44",
            contrastText: "#fff",
        },
        background: {
            default: "#F8F8F8",
        },
        text: {
            secondary: "#7A7E81",
        },
    },
    components: createComponents(
        {
            MuiButtonBase: {
                TouchRippleProps: {
                    style: {
                        color: "rgba(0, 0, 0, 0.87)",
                    },
                },
            },
            MuiButton: {
                disableRipple: true,
            },
            MuiInputBase: {
                spellCheck: false,
            },
            MuiSelect: {
                IconComponent: ExpandMoreIcon,
                variant: "standard",
            },
        },
        {
            MuiBreadcrumbs: {
                ol: {
                    height: "22px",
                    flexWrap: "nowrap",
                },
                li: {
                    overflow: "hidden",
                    minWidth: "16px",
                    textAlign: "center",
                    textOverflow: "ellipsis",
                    "&:first-of-type": {
                        minWidth: "fit-content",
                    },
                    "&:last-child > *": {
                        color: "#f95744",
                    },
                    "&:hover": {
                        textDecoration: "underline",
                    },
                },
            },
            MuiButton: {
                root: {
                    textTransform: "none",
                    outline: "0 !important",
                    borderRadius: "2px",
                },
                contained: {
                    boxShadow: "none",
                    "&:hover": {
                        backgroundColor: "#f95f4d",
                        boxShadow: "none",
                    },
                    "&:active": {
                        backgroundColor: "#D94734",
                        boxShadow: "none",
                    },
                    "&:disabled": {
                        backgroundColor: "#d8b2a1",
                        boxShadow: "none",
                    },
                },
                containedPrimary: {
                    boxShadow: "none",
                    "&:hover": {
                        backgroundColor: "#f95f4d",
                        boxShadow: "none",
                    },
                    "&:active": {
                        backgroundColor: "#D94734",
                        boxShadow: "none",
                    },
                    "&:disabled": {
                        backgroundColor: "#d8b2a1",
                        boxShadow: "none",
                        color: "#FFFFFF",
                    },
                },
                containedSecondary: {
                    boxShadow: "none",
                    backgroundColor: "#EFEFEF",
                    color: "#898989",
                    "&:hover": {
                        backgroundColor: "#F2F3F4",
                        boxShadow: "none",
                    },
                    "&:active": {
                        backgroundColor: "#DFDFDF",
                        boxShadow: "none",
                    },
                    "&:disabled": {
                        backgroundColor: "#F0F0F0",
                        boxShadow: "none",
                        color: "#9EA1A2",
                    },
                },
                text: {
                    color: "#f95744",
                    transition: "color, .15s ease-in",
                    "& > span > svg": {
                        transition: "color, .15s ease-in",
                    },
                    "&:hover": {
                        backgroundColor: "transparent",
                        color: "#FF9797",
                        "& > span > svg": {
                            color: "#FF9797",
                        },
                        "&:active": {
                            color: "#f95744",
                            "& > span > svg": {
                                color: "#f95744",
                            },
                        },
                    },
                },
                textPrimary: {
                    "&:hover": {
                        backgroundColor: "transparent",
                        color: "#FF9797",
                        "& > span > svg": {
                            color: "#FF9797",
                        },
                        "&:active": {
                            color: "#f95744",
                            "& > span > svg": {
                                color: "#f95744",
                            },
                        },
                    },
                },
            },
            MuiDialog: {
                root: {
                    backgroundColor: "rgba(127, 175, 214, 0.2)",
                },
            },
            MuiDivider: {
                root: {
                    border: 0,
                    height: "1px",
                    backgroundColor: "rgba(0, 0, 0, 0.12)",
                },
            },
            MuiDrawer: {
                paper: {
                    width: "350px",
                    backgroundColor: "#EDEFF1",
                    border: "none !important",
                    [breakpoints.down("sm")]: {
                        width: "240px",
                    },
                },
            },
            MuiFormHelperText: {
                root: {
                    fontStyle: "italic",
                    marginLeft: "12px",
                    "&::before": {
                        content: "'*'",
                    },
                },
            },
            MuiListItem: {
                root: {
                    "&:hover": {
                        color: "#f95744",
                        "& > div": {
                            color: "#f95744",
                        },
                    },
                    "&.Mui-selected": {
                        color: "#f95744",
                        backgroundColor: "transparent",
                        "&:hover": {
                            backgroundColor: "transparent",
                        },
                        "& > div": {
                            color: "#f95744",
                        },
                    },
                },
                button: {
                    "&:hover": {
                        backgroundColor: "#F7F8F8",
                    },
                },
            },
            MuiListItemIcon: {
                root: {
                    minWidth: "auto",
                    color: "#91A5B9",
                    "&> svg": {
                        color: "inherit",
                    },
                },
            },
            MuiListItemText: {
                root: {
                    color: "#91A5B9",
                },
            },
            MuiNativeSelect: {
                icon: {
                    color: "#B5B5B6",
                    marginRight: "10px",
                    width: "20px",
                },
                select: {
                    "&:focus": {
                        backgroundColor: "white",
                    },
                },
            },
            MuiSvgIcon: {
                root: {
                    color: "#91A5B9",
                },
            },
            MuiSelect: {
                root: {
                    "&:hover": {
                        color: "#FF9797",
                        border: "1px solid #FFA6AB",
                        "& ~ svg": {
                            color: "#FF9797",
                        },
                        "& span": {
                            color: "#FF9797",
                        },
                    },
                    "&:active": {
                        color: "#f95744",
                        border: "1px solid #f95744",
                        "& ~ svg": {
                            color: "#f95744",
                        },
                    },
                    "&.Mui-disabled": {
                        background: "#EDEDED",
                        color: "#949495",
                        "& ~ svg": {
                            color: "#949495",
                        },
                        "&:hover": {
                            borderColor: "#E6E6E6",
                            color: "#949495",
                            "& ~ svg": {
                                color: "#949495",
                            },
                            "&:active": {
                                color: "#949495",
                                borderColor: "#E6E6E6",
                                "& ~ svg": {
                                    color: "#949495",
                                },
                            },
                        },
                    },
                },
                select: {
                    color: "#949495",
                    border: "1px solid #E6E6E6",
                    borderRadius: "2px",
                    "&:focus": {
                        color: "#f95744",
                        border: "1px solid #f95744",
                        borderRadius: "2px",
                        "& ~ svg": {
                            color: "#f95744",
                        },
                        "& span": {
                            color: "#f95744",
                        },
                    },
                    "&.Mui-disabled": {
                        border: "1px solid #E6E6E6",
                    },
                },
                icon: {
                    color: "#949495",
                    right: "9px",
                },
            },
            MuiSnackbarContent: {
                root: {
                    backgroundColor: "rgba(33, 42, 64, 0.7)",
                    borderRadius: "2px",
                },
            },
            MuiMenuItem: {
                root: {
                    color: "#91A5B9",
                    fontSize: "16px",
                    height: "50px",
                    paddingTop: "15px",
                    paddingBottom: "15px",
                    "& div": { padding: "0px" },
                    "& span": { margin: "auto" },
                    "&:hover": {
                        backgroundColor: "#F7F8F8",
                    },
                    "&.Mui-selected": {
                        "& div": { color: "#f95744" },
                        backgroundColor: "transparent",
                        color: "#f95744",
                    },
                },
            },
            MuiInputLabel: {
                outlined: {
                    color: "#AAABAB",
                    transform: "translate(14px, 15px) scale(1)",
                    "&.MuiInputLabel-shrink": {
                        transform: "translate(14px, -9px) scale(0.75)",
                    },
                },
            },
            MuiInputBase: {
                root: {
                    color: "#949495",
                    "&.Mui-error": {
                        "& > .MuiSelect-root": {
                            borderColor: "#f44",
                        },
                    },
                    "&.Mui-disabled": {
                        border: "none",
                        color: "#797E80",
                    },
                },
                input: {
                    border: "0px solid #E6E6E6",
                    borderRadius: "2px",
                    backgroundColor: "#ffffff",
                    padding: "9px 13px",
                    "&:focus": {
                        borderColor: "#f95744",
                    },
                    "&:disabled": {
                        color: "#797E80",
                        backgroundColor: "#EDEDED",
                        borderColor: "transparent",
                    },
                    "&::placeholder": {
                        color: "#AAABAB",
                        opacity: 1,
                    },
                },
            },
            MuiFormLabel: {
                root: {
                    lineHeight: "unset",
                },
            },
            MuiTextField: {
                root: {
                    "& fieldset": {
                        borderRadius: 2,
                        borderWidth: "1 !important",
                        borderColor: "#E6E6E6",
                        "&:hover": {
                            border: "none",
                        },
                        "&:disabled": {
                            border: "none",
                        },
                    },
                    "&:disabled fieldset": {
                        border: "red",
                    },
                    "& > div": {
                        "&:hover": {
                            border: "none",
                        },
                    },
                },
            },

            MuiOutlinedInput: {
                root: {
                    "&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline":
                        {
                            borderColor: "#FF6F88",
                        },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline:not(.Mui-error)":
                        {
                            borderWidth: "1px",
                        },
                    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                        borderColor: "transparent",
                    },
                },
                input: {
                    height: "20px",
                    padding: "9px 13px",
                    fontSize: "14px",
                },
            },
            MuiLink: {
                root: {
                    transition: "color, .15s ease-in",
                    outline: 0,
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    "&:hover": {
                        color: "#FF9797",
                    },
                    "&:active": {
                        color: "#f95744",
                    },
                },
            },
            MuiCheckbox: {
                colorPrimary: {
                    "&.Mui-checked": {
                        "& > span > svg": {
                            color: "#f95744",
                        },
                    },
                },
                root: {
                    "&.Mui-checked": {
                        "& > span > svg": {
                            color: "#f95744",
                        },
                    },
                },
            },
            MuiSwitch: {
                root: {
                    width: "66px",
                },
                switchBase: {
                    padding: "4px",
                    "&:hover": {
                        // remove hover shadow
                        backgroundColor: "transparent",
                    },
                    "&.Mui-checked": {
                        "& + .MuiSwitch-track": {
                            opacity: 1,
                            backgroundColor: "#FFC7A0 !important",
                        },
                        "& .MuiSwitch-thumb": {
                            backgroundColor: "#f95744",
                        },
                    },
                    "&.Mui-disabled": {
                        "& + .MuiSwitch-track": {
                            opacity: 1,
                        },
                        "& .MuiSwitch-thumb": {
                            backgroundColor: "#e6e6e6",
                        },
                    },
                    "&.Mui-disabled.Mui-checked": {
                        "& .MuiSwitch-thumb": {
                            backgroundColor: "#E99473",
                        },
                    },
                },
                thumb: {
                    height: "30px",
                    width: "30px",
                    backgroundColor: "#D6D6D6",
                    boxShadow: "none",
                },
                track: {
                    backgroundColor: "#EDEDED !important",
                    height: "18px",
                    opacity: 1,
                },
            },
            MuiTabs: {
                root: {
                    minHeight: "38px !important",
                },
                indicator: {
                    backgroundColor: "#f95744",
                    height: "2px",
                },
            },
            MuiTab: {
                root: {
                    height: "38px !important",
                    minHeight: "38px !important",
                    minWidth: "90px !important",
                    outline: "none !important",
                    "&.Mui-selected": {
                        color: "#f95744",
                    },
                    textTransform: "initial",
                },
                textColorInherit: {
                    color: "#797E80",
                },
            },
            MuiTableSortLabel: {
                root: {
                    color: "#9FA0A0",
                    "& p": {
                        fontSize: "15px",
                    },
                    "&.Mui-active": {
                        color: "#f95c49 !important",
                    },
                },
                icon: {
                    color: "inherit !important",
                },
            },
        },
    ),
} as any
export const MaterialBright = createTheme(brightSettings)

export const MaterialDark = createTheme({
    palette: {
        primary: {
            light: "#ffffff",
            main: "#e0e0e0",
            dark: "#aeaeae",
            contrastText: "#fff",
        },
        secondary: {
            light: "#efefef",
            main: "#bdbdbd",
            dark: "#8d8d8d",
            contrastText: "#1a1a1a",
        },
        tertiary: {
            light: "#335d73",
            main: "#00263c",
            dark: "#000e1a",
            contrastText: "#fff",
        },
        error: {
            light: "#f44",
            main: "#f44",
            dark: "#f44",
            contrastText: "#fff",
        },
        mode: "dark",
    },
    components: createComponents(
        {},
        {
            MuiDrawer: {
                paper: {
                    width: "350px",
                },
            },
            MuiMenu: {
                paper: {
                    backgroundColor: "#616161",
                },
            },
            MuiMenuItem: {
                root: {
                    borderRadius: "5px",
                    color: "#fff",
                    fontSize: "16px",
                    marginTop: "0.4em",
                    marginBottom: "0.4em",
                    padding: "0.5em 0.3em 0.5em 0.3em",
                    "& div": { padding: "0px", width: "20%" },
                    "& span": { margin: "auto" },
                    "& div, span": { color: "#fff" },
                    "&:hover": {
                        "& div, span": { color: "#f95744 !important" },
                    },
                    "&.Mui-selected": {
                        "& div, span": { color: "white !important" },
                        color: "#ffffff !important",
                        backgroundColor: "#f95744 !important",
                    },
                },
            },
            MuiSvgIcon: {
                root: {
                    color: "white",
                },
            },
            MuiSwitch: {
                root: {
                    width: "66px",
                },
                switchBase: {
                    padding: "4px",
                    "&:hover": {
                        // remove hover shadow
                        backgroundColor: "transparent !important",
                    },
                },
                thumb: {
                    height: "30px",
                    width: "30px",
                },
            },
            MuiTabs: {
                indicator: {
                    backgroundColor: "transparent",
                },
            },
            MuiTab: {
                root: {
                    minWidth: "120px !important",
                    "&.Mui-selected": {
                        backgroundColor: "#f95c49",
                        borderRadius: "3px",
                        color: "white",
                    },
                    textTransform: "initial",
                },
            },
        },
    ),
} as any)
